import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ComponentFactoryResolver, Inject, Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { URLS } from '@base/classes/btl-constants';
import { BtlHelperService } from '@base/services/btl-helper.service';
import { BytelTagService } from '@base/services/bytel-tag.service';

import { FilterTop } from '@catalog/category/widgets/filter/filter.interface';
import { ProductsService } from '@catalog/products.service';
import { Catalog } from '@catalog/products/catalog';
import { Phone } from '@catalog/products/equipement/complex/phone';
import { JsonCatalog, JsonProduct, PlanPrices } from '@catalog/products/interface/context';
import { Product } from '@catalog/products/product';
import { Edp } from '@catalog/products/subscription/edp';
import { Plan } from '@catalog/products/subscription/plan';
import { Sensation } from '@catalog/products/subscription/plan/premium/sensation';
import { Simo } from '@catalog/products/subscription/plan/simo';
import { Sowo } from '@catalog/products/subscription/plan/sowo';
import { AvailabilityAlertModalComponent } from '@catalog/products/view/modals/availability-alert-modal/availability-alert-modal.component';
import { ProductFeature, ProductFeatureByKey } from '@catalog/products/view/product-feature.interface';

import { CartHelperService } from '@checkout/cart/helper/cart-helper.service';
import { Scheme } from '@checkout/cart/scheme.class';

import { CustomerContextService } from '@context/child/customer-context.service';
import { RenewContextService } from '@context/child/renew-context.service';

import { MetaDataService } from '@app/base/services/meta-data.service';
import { STOCK_STATUS } from '@app/baseBtl/catalog/products/Enum';
import { SensationPro } from '@app/catalog/products/subscription/plan/pro/sensation';
import { SimoPro } from '@app/catalog/products/subscription/plan/pro/simo';
import { AbstractProductClass } from '@app/modules/product-wall/services/factory/abstract-product.class';
import { ProviderCheckoutService } from '@app/provider/services/provider-checkout.service';

import { ConfigurationService } from '@common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalRef, ModalService } from '@trilogy';
import { BehaviorSubject, Observable, from, of as observableOf } from 'rxjs';

import { IDeviceConf } from '../../helpers/IDeviceConf';
import { IShowSection } from '../../helpers/IShowSection';
import { PhonesStorageService } from '../phones-storage.service';

export interface PriceProperties {
    price?: string;
    oldPrice?: string;
    promoDuration?: string;
}

@Injectable({
    providedIn: 'root',
})
export class BaseProduct implements AbstractProductClass {
    public productType: any;
    public cmsBlockId: string = '';
    public cmsBlockIdNotFound: string = '';
    public legalInformationBlockId: string = '';
    public cmsWallLegalInformation: string;
    public showAdditionalLegalInformation: boolean = false;
    public canShowWallTabs: boolean = true;
    public link: string;
    public breadcrumbName: string;
    public phonePrice: number;
    public defaultUrlPlan: string;
    public categoryCode: string;
    public wallPlans: string[] = [];
    public wallTitle: string = '';
    public sliderName: string;
    public seeOtherPlans: string;
    public defaultPlanIdType;
    public plansInCart: JsonProduct = null;
    public planType: string;
    public storedConfigurator: { [key: string]: any };
    public customerContext: CustomerContextService;
    public canShowSection: IShowSection;
    public hasDefaultPlan: boolean = true;
    public canShowEdp: boolean;
    public canShowSlider: boolean;
    public canShowPriceAlone: boolean;
    public canShowProductTitle: boolean;
    public canCountOnlyPlanPro: boolean;
    public hasSinglePrice: boolean;
    public hasReachedWall: boolean = false;
    public isRenewal: boolean = false;
    public PHONES_WALL_URL: string;
    public cardType: string;
    public wallType: string;
    public wallUrl: string;
    public wallProUrl: string;
    public wallWording: string;
    public globalFilters: boolean = false;
    public priceFilterPosition: number = 1;
    public hiddenSort: string[] = [];
    public tagTitle: string;
    public retour: string;
    public isReco: boolean = false;
    public hasLandscapeThumb: boolean = false;
    public titleTheme: string = 'trilogy';
    protected animCoAttribute: string = 'anim_co';
    public showFilters: boolean = true;
    public canShowActiveLocator: boolean = false;
    public device = {
        canShowComparator: true,
        colorchipsClass: true,
        canShowDas: true,
        canShowPricesFrom: true,
    } as IDeviceConf;
    public canShowPercent: boolean = false;
    public canShowCrossedPrice: boolean = false;
    public filterTopHasArrow: boolean = true;
    public canShowCharacteristic: boolean = false;
    public canShowAnimCo: boolean = false;
    public canShowPlansTab: boolean = false;
    public isWithoutTax: boolean;
    public numberFix: number = 0;
    public canShowConfigurators: boolean = false;
    public canShowGooglePopin: boolean;
    public canShowExpressDelivery: boolean = false;
    // supprime le lien href filtre marque
    public canShowLinkManufacturer: boolean = true;
    public canShowBrandInBreadcrumb: boolean = false;
    public userModalClicked: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public canShowColor: boolean = false;
    public indexedPage: boolean = true;
    public canShowHandsFreeKit: boolean = false;
    public canShowRecoveryConfiguratorEntry = false;
    public deviceType: string = 'téléphone';
    public wordingReinsuranceTitle: string = 'Livraison à domicile le lendemain';
    public wordingReinsuranceSubtitle: string = 'ou livraison express gratuite en point relais';
    public productIcon;
    protected listPlanProductDetail = [Sensation.PLAN_TYPE, Sowo.PLAN_TYPE, Simo.PLAN_TYPE];
    protected providerCheckoutService = inject(ProviderCheckoutService);

    constructor(
        protected config: ConfigurationService,
        protected tagService: BytelTagService,
        protected btlHelper: BtlHelperService,
        protected phonesStorage: PhonesStorageService,
        protected modalService: NgbModal,
        protected modalServiceTrilogy: ModalService,
        protected renewContextService: RenewContextService,
        protected cartHelper: CartHelperService,
        protected http: HttpClient,
        protected componentFactoryResolver: ComponentFactoryResolver,
        protected productService: ProductsService,
        @Inject(DOCUMENT) protected document: Document,
        protected metaDataService: MetaDataService,
    ) {
        this.metaDataService.useCanonicalUrl();
    }

    public getPlanType(): string {
        const urlPlanType = this.getUrlPlanType(document);
        let planType;

        if (urlPlanType) {
            planType = urlPlanType;
        } else if (this.wallPlans.includes(this.cartHelper.getPlanType())) {
            planType = this.cartHelper.getPlanType();
            const plan = this.cartHelper.getPlan();
            if (plan?.categories.includes(Simo.PLAN_TYPE)) {
                planType = Simo.PLAN_TYPE;
            }
        } else {
            planType = this.defaultUrlPlan;
        }

        if (this.cartHelper.isPro()) {
            switch (planType) {
                case Sensation.PLAN_TYPE:
                    planType = SensationPro.PLAN_TYPE;
                    break;
                case Simo.PLAN_TYPE:
                    planType = SimoPro.PLAN_TYPE;
                    break;
            }
        }

        this.setPlanType(planType);

        return planType;
    }

    public setPlanType(planType: string): void {
        this.planType = planType;
    }

    public getDisplayPlanDetailsLink(): boolean {
        return false;
    }

    public getProductName(product: JsonProduct): string {
        const manufacturer = parseInt(product.manufacturer) > 0 ? product.manufacturer_name : product.manufacturer;
        return `${manufacturer ? manufacturer + ' ' : ''}${product.name}`;
    }

    public injectRenewalComponent(): boolean {
        return false;
    }

    public setPriceProperty = (): PriceProperties => ({
        price: this.planType === Sowo.PLAN_TYPE ? 'price' : 'realPrice',
        oldPrice: this.planType === Sowo.PLAN_TYPE ? 'oldPrice' : 'realOldPrice',
        promoDuration: this.planType === Sowo.PLAN_TYPE ? 'promoDuree' : 'promoRealDuree',
    });

    public addEdp(phone: Product, scheme: Scheme): void {
        if (phone instanceof Phone) {
            if (this.planType === Sensation.PLAN_TYPE && phone.isEdpAppliable(scheme, this.planType)) {
                scheme.add(Catalog.getInstance(Edp.defaultData, scheme.productContext));
            }
        }
    }

    public getUrlPlanName(): string {
        const paramsStr: string = window.location.search;
        const paramsObj = this.turnParamsIntoObject(paramsStr);
        return paramsObj.forfait || null;
    }

    public getUrlPlan(plans: JsonProduct[], document: Document): JsonProduct {
        const paramsStr: string = document.defaultView.location.search;
        let params;
        let plan = null;

        if (paramsStr) {
            params = this.turnParamsIntoObject(paramsStr);

            if (params.forfait === 'sans') {
                plan = {};
            } else {
                plan = Object.values(plans).filter((planToFilter: JsonProduct) => {
                    const urlSplit = planToFilter.url.split('/');
                    const urlPlan = urlSplit[urlSplit.length - 1];
                    return params.forfait === urlPlan;
                })[0];
            }

            document.defaultView.history.replaceState(null, null, document.defaultView.location.href);
        }
        return plan;
    }

    public getUrlPlanType(document: Document): string | null {
        const paramsStr: string = document.defaultView.location.search;
        const params = this.turnParamsIntoObject(paramsStr);
        let urlPlanType = null;

        if (params && params.forfait) {
            if (params.forfait.indexOf('smartphone') !== -1) {
                urlPlanType = Sensation.PLAN_TYPE;
            } else if (params.forfait.indexOf('sim-seule') !== -1) {
                urlPlanType = Simo.PLAN_TYPE;
            } else if (params.forfait.indexOf('b-you') !== -1) {
                urlPlanType = Sowo.PLAN_TYPE;
            }
        }
        return urlPlanType;
    }

    private turnParamsIntoObject(paramsStr: string): any {
        const params = {};
        paramsStr
            .substring(1)
            .split('&')
            .forEach((item) => {
                params[item.split('=')[0]] = item.split('=')[1];
            });
        return params;
    }

    public actionProduct(
        product: JsonProduct,
        modalService: ModalService,
        btlHelper: BtlHelperService,
        scheme: Scheme,
        cartHelper: CartHelperService,
        noPlanAdded: boolean = false,
    ): void {
        scheme = this.applySalesScheme(scheme);
        switch (product.stock.eresa.status) {
            case STOCK_STATUS.OUT_OF_STOCK:
            case STOCK_STATUS.ONLY_IN_SHOP:
                btlHelper.navigateToExternalURL(
                    `${this.PHONES_WALL_URL}/${this.link}${product.manufacturer_code ? '-' : ''}${
                        product.manufacturer_code
                    }`,
                );
                break;
            case STOCK_STATUS.IN_STOCK:
            case STOCK_STATUS.SOON_OUT_OF_STOCK:
            case STOCK_STATUS.IN_STOCK_FROM:
                this.addToCart(btlHelper, cartHelper, true, scheme, noPlanAdded);
                break;
            case STOCK_STATUS.SOON_AVAILABLE:
                const modalRef: ModalRef<AvailabilityAlertModalComponent> =
                    modalService.open<AvailabilityAlertModalComponent>(AvailabilityAlertModalComponent);
                const modalAlertComponent: AvailabilityAlertModalComponent = modalRef.componentInstance;
                modalAlertComponent.productId = product.entity_id;
                break;
        }
    }

    public applySalesScheme(scheme: Scheme): Scheme {
        return scheme;
    }
    public addToCart(
        btlHelper: BtlHelperService,
        cartHelper: CartHelperService,
        redirect: boolean = true,
        scheme: Scheme = null,
        isPhoneOnly: boolean = false,
    ): void {
        const cartRedirect = cartHelper.getCartRedirect();
        const productsToAdd = scheme.products?.map((p) => p.gencode)?.filter((p) => !p.includes('EPD'));

        from(this.cartHelper.addProductsToCart(productsToAdd, false, null, isPhoneOnly)).subscribe(() => {
            if (redirect) {
                btlHelper.navigateToExternalURL(cartRedirect.url, false, cartRedirect.isSpa);
            }
        });
    }

    public sendWallTags(pageVariante?: string): void {
        this.tagService.send({
            parcours: this.tagTitle,
            page: 'Mur_de_telephone',
            pageCategorie: 'Liste_produits_telephone',
            activiteB2BouB2C: this.isWithoutTax ? 'B2B' : 'B2C',
            etapeEcommerce: 'Liste_produits',
            variablesSpecifiques: {},
            pageVariante,
            url: this.document.defaultView.location.href,
        });
    }

    public sendTopFilterTagEvent(attr: FilterTop): void {
        const nomClic = BtlHelperService.formatForTracking(`${attr.value}_mur_${this.wallType}`);
        this.tagService.send({
            nomClic,
            categorieClic: 'Filtre',
            actionClic: 'Clic_affichage',
        });
    }

    public setProducts(phonesPricesData: JsonProduct, products: JsonCatalog) {
        const productsKeys: string[] = [];
        if (phonesPricesData && Object.keys(phonesPricesData).length) {
            Object.keys(phonesPricesData.products).forEach((key) => {
                if (products[key] && parseInt(phonesPricesData.products[key].price, 0) >= 1) {
                    products[key].prices.premium = phonesPricesData.products[key];
                    productsKeys.push(key);
                } else {
                    delete products[key];
                }
            });
        }

        return productsKeys;
    }

    public isModalActive(): boolean {
        return !this.phonesStorage.getItem(this.phonesStorage.KEY.USER_ACTION_MODAL);
    }

    public getCategoryCode(categoryCode: string): string {
        return categoryCode;
    }

    public setAnimCo(product: JsonProduct): string {
        const animCo = product[this.animCoAttribute];
        if (animCo?.length) {
            return animCo;
        }
        return null;
    }

    public getProductKey(skus: string[], product: JsonProduct) {
        const productKey = skus.find((sku) => product.childs[sku].in_stock);
        return productKey ? productKey : skus[0];
    }

    public setCurrentProduct(skus: string[], parentProduct: JsonProduct, productKey): JsonProduct {
        const key = this.findProduct(skus, parentProduct) || productKey;
        return parentProduct.childs[key];
    }

    public findProduct(skus: string[], parentProduct: JsonProduct): string {
        const searchUrl = this.document.defaultView.location.href
            .replace(this.document.defaultView.location.hash, '')
            .replace(this.document.defaultView.location.search, '');

        for (const sku of skus) {
            const url: string = parentProduct.childs[sku].url || null;
            if (searchUrl.split('/').pop() === url.split('/').pop()) {
                return sku;
            }
        }
        return null;
    }

    public setProductChilds(product: JsonProduct) {
        if (!product.childs) {
            return { [product.gencode]: { ...product } };
        }

        const skus = Object.keys(product.childs);

        for (const sku of skus) {
            product.childs[sku].bonus_recovery = product.bonus_recovery;
            product.childs[sku].recovery_from_date = product.recovery_from_date;
            product.childs[sku].recovery_to_date = product.recovery_to_date;
        }

        return product.childs;
    }

    public getFeatures(skus: string[], product: JsonProduct): ProductFeatureByKey {
        const features: ProductFeatureByKey = {
            colors: {
                order: 0,
                title: 'fiche_tel.select-color',
                defaultTitle: 'Sélectionner la couleur',
                key: 'color_code',
                isColor: true,
                urls: {},
                values: [],
                availableValues: [],
            } as ProductFeature,
            capacities: {
                order: 1,
                title: 'fiche_tel.select-memory',
                defaultTitle: 'Sélectionner la mémoire',
                key: 'capacity',
                isColor: false,
                urls: {},
                values: [],
                availableValues: [],
            } as ProductFeature,
        };
        for (const sku of skus) {
            const capacity: string = product.childs[sku].capacity || null;
            const color: string = product.childs[sku].color_code || null;

            if (capacity && !features.capacities.values.includes(capacity)) {
                features.capacities.values.push(capacity);
            }

            if (color && !features.colors.values.includes(color)) {
                features.colors.values.push(color);
            }
        }

        this.canShowSection.canShowColor =
            (features.colors.values && features.colors.values.length > 1) || this.cardType === 'Acquisition';

        return features;
    }

    public getFeaturesNewton(skus: string[], product: JsonProduct): ProductFeatureByKey {
        const features: ProductFeatureByKey = {
            colors: {
                order: 0,
                title: 'fiche_tel.select-color',
                defaultTitle: 'Je choisis la couleur',
                key: 'color',
                isColor: true,
                urls: {},
                values: [],
                availableValues: [],
            } as ProductFeature,
            colors_code: {
                order: 0,
                title: 'fiche_tel.select-color_code',
                defaultTitle: 'Sélectionner la couleur',
                key: 'color_code',
                isColor: false,
                urls: {},
                values: [],
                availableValues: [],
            } as ProductFeature,
            capacities: {
                order: 1,
                title: 'fiche_tel.select-memory',
                defaultTitle: 'Je choisis la mémoire',
                key: 'capacity',
                isColor: false,
                urls: {},
                values: [],
                availableValues: [],
            } as ProductFeature,
        };
        for (const sku of skus) {
            const capacity: string = product.childs[sku].capacity || null;
            const color_code: string = product.childs[sku].color_code || null;
            const color: string = product.childs[sku].color || null;

            if (capacity && !features.capacities.values.includes(capacity)) {
                features.capacities.values.push(capacity);
            }

            if (color && !features.colors.values.includes(color)) {
                features.colors.values.push(color);
                features.colors.availableValues.push(color);
            }

            if (color && !features.colors_code.values.includes(color_code)) {
                features.colors_code.values.push(color_code);
            }
        }

        this.canShowSection.canShowColor =
            (features.colors.values && features.colors.values.length > 1) || this.cardType === 'Acquisition';

        return features;
    }

    public removeSkeleton(): void {
        const skeletonElement: HTMLElement = this.document.querySelector('div[id^=skeleton-]');
        if (skeletonElement) {
            skeletonElement.parentNode.removeChild(skeletonElement);
        }
    }

    public updateUrls(features: ProductFeatureByKey, product: JsonProduct, isColor: boolean, value): void {
        if (!value) {
            return;
        }
        const childs = product.childs;
        const skus = Object.keys(childs);
        if (isColor) {
            features.capacities.urls = {};
        } else {
            features.colors.urls = {};
        }

        for (const sku of skus) {
            const capacity: string = childs[sku].capacity || null;
            const color: string = childs[sku].color_code || null;
            const url: string = childs[sku].url || null;

            if (color === value && isColor) {
                features.capacities.urls[capacity] = url;
            }
            if (capacity === value && !isColor) {
                features.colors.urls[color] = url;
            }
        }
        // Not all capacities are avialable in all colors.
        // We must check for empty url and then look for the next avialable capacity in that color
        if (!isColor) {
            this.checkEmptyUrls(features, product);
        }
    }

    private checkEmptyUrls(features: ProductFeatureByKey, product: JsonProduct): void {
        const childs = product.childs;
        const skus = Object.keys(childs);
        for (const sku of skus) {
            const color: string = childs[sku].color_code || null;
            if (!features.colors.urls[color]) {
                const skuWithSameColor = skus.filter((key) => childs[key].color_code === color);
                skuWithSameColor.sort((a, b) => this.stringToIntSort(a, b));
                features.colors.urls[color] = childs[skuWithSameColor[0]].url;
            }
        }
    }

    public stringToIntSort(a: string, b: string): number {
        let itemA: number = parseInt(a, 10);
        let itemB: number = parseInt(b, 10);

        itemA = a.includes('To') ? itemA * 1024 : itemA;
        itemB = b.includes('To') ? itemB * 1024 : itemB;

        if (itemA > itemB) {
            return 1;
        } else if (itemA < itemB) {
            return -1;
        } else {
            return 0;
        }
    }

    public updateCapacitiesByColor(
        features: ProductFeatureByKey,
        product: JsonProduct,
        color_code: string,
    ): ProductFeatureByKey {
        const childs = product.childs;
        const skus = Object.keys(childs);
        features.capacities.availableValues = [];

        for (const sku of skus) {
            const capacity: string = childs[sku].capacity || null;
            const colorChild: string = childs[sku].color || null;
            const colorCodeChild: string = childs[sku].color_code || null;

            if (
                (color_code === colorCodeChild || color_code === colorChild) &&
                !features.capacities.availableValues.includes(capacity)
            ) {
                features.capacities.availableValues.push(capacity);
            }
        }
        features.capacities.availableValues.sort((a, b) => this.stringToIntSort(a, b));
        return features;
    }

    public isNakedPriceOnly(selectedPlanType?: string): boolean {
        const plansWithNakedPrice = ['plan_simo', 'plan_sowo'];
        return (
            plansWithNakedPrice.includes(selectedPlanType) ||
            this.canShowCrossedPrice ||
            (this.cardType === 'Acquisition' && !selectedPlanType)
        );
    }

    public getProductUrl(url: string): string {
        const urlPlan = this.getUrlPlanName();
        const urlWall = this.isProContext() ? this.wallProUrl : this.wallUrl;
        return `${urlWall}/${url.split('/').pop()}${urlPlan ? '?forfait=' : ''}${urlPlan || ''}`;
    }

    public getCartTypeFromProduct(product: JsonProduct): string {
        const type = product.type_id.toLowerCase();
        switch (type) {
            case 'accessory_configurable':
            case 'accessory':
                return URLS.CARD_TYPE.ACCESSORY;
            case 'phone':
            case 'phone_simple':
                return URLS.CARD_TYPE.PHONE;
            default:
                return type;
        }
    }

    public getListGencodeToFilter(): Observable<any> {
        return observableOf([]);
    }

    getPlanTypeArray(urlPlanType?: string | null): string[] {
        return [];
    }

    getProductWallPrices(device: JsonProduct, plan: Plan): PlanPrices {
        return undefined;
    }

    goToProductPage(product?: JsonProduct, userData?: CustomerContextService): void {
        return undefined;
    }

    public getProductPricesParams(cartPlan?: JsonProduct): object {
        return {};
    }

    public sendDeviceClickTags(product: JsonProduct, isReco: boolean = false) {
        // implemented in children
    }
    public cleanUnauthorizedQuote(): void {
        // implemented in phone and renew
    }

    public getWallURL(activatedRoute: ActivatedRoute): string {
        return activatedRoute.snapshot.data.type === 'pro' ? this.wallProUrl : this.wallUrl;
    }

    public isProContext(): boolean {
        return false;
    }
}
